import { useState } from "react";
import { useLocation } from "react-router-dom";
import { ExternalLink, HelpCircle, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspace } from "@/hooks/useWorkspace";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

type FeedbackDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/**
 * Controlled Send-feedback dialog. Used by the desktop floating help FAB
 * (HelpWidget) and the mobile More sheet entry.
 */
export function FeedbackDialog({ open, onOpenChange }: FeedbackDialogProps) {
  const { user } = useAuth();
  const { active } = useWorkspace();
  const location = useLocation();
  const [category, setCategory] = useState("question");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  if (!user) return null;

  const submit = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error("Subject and body are required");
      return;
    }
    setBusy(true);
    const { error } = await supabase.from("support_tickets").insert({
      user_id: user.id,
      workspace_id: active?.id ?? null,
      category,
      subject: subject.trim().slice(0, 200),
      body: body.trim().slice(0, 5000),
      page_path: location.pathname + location.search,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : null,
    });
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Thanks — we got your feedback.");
    setSubject("");
    setBody("");
    setCategory("question");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Send feedback</DialogTitle>
          <DialogDescription>
            Found a bug, have an idea, or stuck on something? We read every message.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Type</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="bug">Bug report</SelectItem>
                <SelectItem value="idea">Feature idea</SelectItem>
                <SelectItem value="question">Question</SelectItem>
                <SelectItem value="billing">Billing</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} />
          </div>
          <div>
            <Label>Details</Label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} maxLength={5000} />
            <p className="mt-1 text-xs text-muted-foreground">
              We'll automatically include the page you're on ({location.pathname}).
            </p>
          </div>
        </div>
        <div className="border-t border-border pt-3 text-xs text-muted-foreground">
          Checking if something is down?{" "}
          <a
            href="https://stats.uptimerobot.com/t1k8OOIxf4"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-foreground underline-offset-2 hover:underline"
          >
            System status
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => void submit()} disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Floating help FAB. Hidden on mobile (md:hidden inverted) so it doesn't
 * overlap content; mobile users reach the same dialog via the More sheet in
 * the bottom tab bar.
 */
export function HelpWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  if (!user) return null;

  return (
    <>
      <Button
        variant="default"
        size="icon"
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-4 z-40 hidden h-12 w-12 rounded-full shadow-lg md:flex"
        aria-label="Help & feedback"
      >
        <HelpCircle className="h-5 w-5" />
      </Button>
      <FeedbackDialog open={open} onOpenChange={setOpen} />
    </>
  );
}
